import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorProfileApi } from '../api/client';

// Phase 3.0 — "Match Me": a vendor describes their company (pasted text or a website
// URL); the system extracts a capability profile they can edit, saves it, and ranks
// ACTIVE federal opportunities that fit. Matching is advisory and grounded in the SAM
// library; the pursuit decision (Bid/No-Bid) stays with the vendor.

// Editable list fields shown as removable chips + an "add" input.
const CHIP_FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'naics_titles', label: 'Industry titles (NAICS)', hint: 'e.g. Computer Systems Design Services' },
  { key: 'core_expertise', label: 'Core expertise', hint: 'e.g. cybersecurity engineering' },
  { key: 'products_services', label: 'Products & services', hint: 'e.g. incident response' },
  { key: 'keywords', label: 'Keywords', hint: 'short terms a matcher should use' },
  { key: 'domains', label: 'Domains', hint: 'e.g. DoD, civilian agencies' },
  { key: 'technologies', label: 'Technologies', hint: 'e.g. AWS, Kubernetes' },
  { key: 'differentiators', label: 'Differentiators', hint: 'e.g. FedRAMP experience' },
];

const fitColor = (n: number | null | undefined) => {
  if (n == null) return { bg: '#f1f5f9', fg: '#334155' };
  if (n >= 75) return { bg: '#dcfce7', fg: '#166534' };
  if (n >= 50) return { bg: '#fef9c3', fg: '#854d0e' };
  return { bg: '#fee2e2', fg: '#991b1b' };
};

export default function MatchMe() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [summary, setSummary] = useState('');

  const [matching, setMatching] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [matches, setMatches] = useState<any[] | null>(null);
  const [candidateCount, setCandidateCount] = useState<number | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Per-result relevance feedback: notice_id -> 'relevant' | 'not'
  const [feedback, setFeedback] = useState<Record<string, 'relevant' | 'not'>>({});

  const sendFeedback = async (m: any, relevant: boolean) => {
    const key = m.notice_id || m.title;
    setFeedback(prev => ({ ...prev, [key]: relevant ? 'relevant' : 'not' }));
    try {
      await vendorProfileApi.matchFeedback({
        notice_id: m.notice_id, title: m.title, naics: m.naics, psc: m.psc,
        agency: m.agency, relevant,
      });
    } catch { /* optimistic; keep the UI state even if the call fails */ }
  };

  // In-app notice view: open the record in our SAM Library (deep-link by record id,
  // falling back to notice_id). NOT the external sam.gov site.
  const noticeHref = (m: any) => {
    const id = m?.id || m?.notice_id;
    return id ? `/sam-library?award=${encodeURIComponent(id)}` : null;
  };

  // Load any previously saved profile so returning vendors see their profile + can re-match.
  const loadSaved = useCallback(async () => {
    try {
      const res = await vendorProfileApi.get();
      const cp = res?.capability_profile;
      if (cp && Object.keys(cp).length) {
        setProfile(cp);
        setSummary(cp.summary || '');
        setSavedAt(res.updated_at ? Number(res.updated_at) : null);
      }
    } catch { /* no saved profile yet — fine */ }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const findOpportunities = async () => {
    setError(''); setNotice('');
    setMatching(true); setMatches(null); setCandidateCount(null);
    try {
      // Match against the profile currently on screen (so unsaved edits still count).
      const res = await vendorProfileApi.matchOpportunities(profile ? { ...profile, summary } : undefined);
      setMatches(Array.isArray(res?.matches) ? res.matches : []);
      setCandidateCount(res?.candidate_count ?? null);
      if (res?.note) setNotice(res.note);
    } catch (err: any) {
      setError(err?.message || 'Matching failed.');
    }
    setMatching(false);
  };

  const hasProfile = profile && CHIP_FIELDS.some(f => Array.isArray(profile[f.key]) && profile[f.key].length);

  return (
    <div style={{ maxWidth: 980 }}>
      <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>
        Active federal opportunities ranked against your saved capability profile. Matches are
        advisory; take the strong ones into Bid / No-Bid.
      </p>

      {/* No profile yet → send them to My Capabilities (single place to build it). */}
      {!hasProfile ? (
        <section style={cardStyle}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, color: '#0f172a' }}>Set up your capability profile first</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
            Match Me ranks opportunities using your capability profile. Build it once in
            <strong> My Capabilities</strong> (from a description, your SAM.gov record, and your website),
            then come back here for matches.
          </p>
          <button onClick={() => navigate('/seller-onboarding')} style={btn(false)}>
            Go to My Capabilities →
          </button>
        </section>
      ) : (
        /* Profile exists → compact summary + refresh, no duplicate builder. */
        <section style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Matching for your profile</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {summary || (Array.isArray(profile.core_expertise) ? profile.core_expertise.slice(0, 4).join(', ') : 'Your saved capabilities')}
              </div>
            </div>
            <button onClick={findOpportunities} disabled={matching} style={btn(matching, '#0f766e')}>
              {matching ? 'Finding…' : (matches ? 'Refresh matches' : 'Find opportunities')}
            </button>
            <button onClick={() => navigate('/seller-onboarding')}
              style={{ padding: '10px 16px', background: 'white', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Edit capabilities
            </button>
          </div>
        </section>
      )}

      {error && <div role="alert" style={alertStyle('#991b1b', '#fee2e2')}>{error}</div>}
      {notice && !error && <div style={alertStyle('#166534', '#dcfce7')}>{notice}</div>}

      {/* ── ranked matches ── */}
      {matches && (
        <section style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#64748b', margin: '4px 0 12px' }}>
            {matches.length > 0
              ? <><strong>{matches.length}</strong> strong match{matches.length === 1 ? '' : 'es'} (fit 60+) from <strong>{candidateCount ?? '—'}</strong> active opportunities scanned.</>
              : 'No strong matches yet — try broadening your industry titles or keywords.'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {matches.map((m, i) => {
              const fc = fitColor(m.fit_score);
              return (
                <div key={m.notice_id || i} style={{ ...cardStyle, marginTop: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ background: fc.bg, color: fc.fg, fontWeight: 700, fontSize: 13,
                                   padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                      {m.fit_score == null ? 'MATCH' : `Fit ${m.fit_score}`}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                        {noticeHref(m)
                          ? <span onClick={() => navigate(noticeHref(m)!)} style={{ color: '#0f172a', cursor: 'pointer' }}>
                              {m.title || 'Untitled opportunity'} <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 500 }}>· view</span>
                            </span>
                          : (m.title || 'Untitled opportunity')}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {m.agency && <span>{m.agency}</span>}
                        {m.naics && <span>· {m.naics}</span>}
                        {m.notice_type && <span>· {m.notice_type}</span>}
                        {m.response_date && <span>· due {m.response_date}</span>}
                        {m.notice_id && <span style={{ fontFamily: 'monospace', fontSize: 11 }}>· {m.notice_id}</span>}
                      </div>
                      {m.set_aside && <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>{m.set_aside}</div>}
                      {m.why_fit && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#334155' }}><strong>Why it fits:</strong> {m.why_fit}</p>}
                      {m.gaps && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#92400e' }}><strong>Gaps:</strong> {m.gaps}</p>}
                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => navigate('/bid-no-bid')}
                          style={{ padding: '6px 14px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                                   borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Take to Bid / No-Bid →
                        </button>
                        {noticeHref(m) && (
                          <button onClick={() => navigate(noticeHref(m)!)}
                             style={{ padding: '6px 12px', background: 'white', color: '#2563eb', border: '1px solid #bfdbfe',
                                      borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            View notice
                          </button>
                        )}
                        {(() => {
                          const key = m.notice_id || m.title;
                          const fb = feedback[key];
                          return (
                            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>Relevant?</span>
                              <button onClick={() => sendFeedback(m, true)} title="Relevant — teach the matcher"
                                style={{ padding: '5px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                         border: `1px solid ${fb === 'relevant' ? '#16a34a' : '#e2e8f0'}`,
                                         background: fb === 'relevant' ? '#dcfce7' : 'white', color: fb === 'relevant' ? '#166534' : '#64748b' }}>👍</button>
                              <button onClick={() => sendFeedback(m, false)} title="Not a fit — teach the matcher"
                                style={{ padding: '5px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                         border: `1px solid ${fb === 'not' ? '#dc2626' : '#e2e8f0'}`,
                                         background: fb === 'not' ? '#fee2e2' : 'white', color: fb === 'not' ? '#991b1b' : '#64748b' }}>👎</button>
                              {fb && <span style={{ fontSize: 10, color: '#94a3b8' }}>saved</span>}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 20 }}>
        Matches are drawn from active opportunities in the SAM library and scored for fit by an
        AI assistant. They&rsquo;re advisory — verify each solicitation before pursuing.
      </p>
    </div>
  );
}

// ── small presentational helpers ──

const cardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, background: 'white', marginTop: 16,
};

const alertStyle = (fg: string, bg: string): React.CSSProperties => ({
  color: fg, background: bg, padding: '10px 14px', borderRadius: 8, marginTop: 16, fontSize: 13,
});

const btn = (busy: boolean, color = '#4338ca'): React.CSSProperties => ({
  padding: '10px 20px', background: busy ? '#94a3b8' : color, color: 'white', border: 'none',
  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
});

function ModeTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
               border: active ? '1px solid #4338ca' : '1px solid #d1d5db',
               background: active ? '#eef2ff' : 'white', color: active ? '#4338ca' : '#475569' }}
    >
      {label}
    </button>
  );
}

function ChipEditor({ label, hint, values, onAdd, onRemove }:
  { label: string; hint: string; values: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void }) {
  const [draft, setDraft] = useState('');
  const commit = () => { onAdd(draft); setDraft(''); };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {values.map((v, i) => (
          <span key={i} style={{ fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0',
                                 borderRadius: 999, padding: '4px 8px 4px 10px', color: '#334155',
                                 display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {v}
            <button
              onClick={() => onRemove(i)} aria-label={`Remove ${v}`}
              style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
            >×</button>
          </span>
        ))}
        <input
          value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          onBlur={commit}
          placeholder={values.length ? 'add…' : hint}
          style={{ flex: '1 1 140px', minWidth: 120, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }}
        />
      </div>
    </div>
  );
}
